import type { QuestionCustomizationsQuestion } from '../../services/questionCustomizations';

function words(value: string): string[] {
  return value.trim().split(/\s+/).filter(Boolean);
}

function wordingSuffixes(
  duplicate: readonly QuestionCustomizationsQuestion[]
): ReadonlyMap<string, string> {
  const wordings = duplicate.map(question =>
    words(question.question || question.description)
  );
  let commonPrefix = 0;
  while (
    commonPrefix < (wordings[0]?.length ?? 0) &&
    wordings.every(
      wording =>
        wording[commonPrefix]?.toLocaleLowerCase() ===
        wordings[0]?.[commonPrefix]?.toLocaleLowerCase()
    )
  ) {
    commonPrefix += 1;
  }

  let commonSuffix = 0;
  while (
    commonSuffix < (wordings[0]?.length ?? 0) - commonPrefix &&
    wordings.every(wording => {
      const index = wording.length - commonSuffix - 1;
      return (
        index >= commonPrefix &&
        wording[index]?.toLocaleLowerCase() ===
          wordings[0]?.[
            wordings[0].length - commonSuffix - 1
          ]?.toLocaleLowerCase()
      );
    })
  ) {
    commonSuffix += 1;
  }

  return new Map(
    duplicate.map((question, index) => {
      const suffix = wordings[index]
        ?.slice(commonPrefix, wordings[index].length - commonSuffix)
        .slice(-3)
        .join(' ')
        .replace(/^[-\s:;,.–—]+|[-\s:;,.–—?]+$/g, '')
        .trim();
      return [question.qid, suffix ?? ''];
    })
  );
}

/**
 * Keep repeated catalog descriptions readable without exposing QIDs. The
 * source wording is the only place the distinguishing suffix can come from.
 */
export function questionDisplayLabels(
  questions: readonly QuestionCustomizationsQuestion[]
): ReadonlyMap<string, string> {
  const descriptions = new Map<string, QuestionCustomizationsQuestion[]>();
  for (const question of questions) {
    if (!question.qid) continue;
    const description = question.description.trim();
    const duplicate = descriptions.get(description) ?? [];
    duplicate.push(question);
    descriptions.set(description, duplicate);
  }

  const labels = new Map<string, string>();
  for (const question of questions) {
    const description = question.description;
    const duplicate = descriptions.get(description.trim());
    if (!question.qid || !duplicate || duplicate.length < 2) {
      if (question.qid) labels.set(question.qid, description);
      continue;
    }

    const suffix = wordingSuffixes(duplicate).get(question.qid);
    labels.set(
      question.qid,
      suffix ? `${description} (${suffix})` : description
    );
  }
  return labels;
}
